import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM,
  clearCreditTopupCheckoutAttribution,
  readCreditTopupCheckoutAttribution,
} from './creditTopupAnalytics';
import { QueryKey } from '@/Config/api/queryKey';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const SESSION_ID_PARAM = 'session_id';
const TOPUP_STATUS_SUCCESS = 'success';
const TOPUP_STATUS_CANCELLED = 'cancelled';
const TOPUP_STATUS_AUTO_RELOAD_ON = 'auto_reload_on';
const TOPUP_STATUS_AUTO_RELOAD_CANCELLED = 'auto_reload_cancelled';
const TOPUP_STATUS_UNKNOWN = 'unknown';
// The browser can beat Stripe's webhook back to us, so re-read once the grant has had time to land.
const WEBHOOK_SETTLE_MS = 2500;

export const useCreditTopupCheckoutReturn = (): void => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const handledRef = useRef(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Keep timer cleanup independent because setSearchParams reruns the handling effect.
  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  const status = searchParams.get(CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM);
  const sessionId = searchParams.get(SESSION_ID_PARAM);

  useEffect(() => {
    if (!status || handledRef.current) return;
    handledRef.current = true;
    const attribution = readCreditTopupCheckoutAttribution();
    const purchaseAttribution = attribution?.flow === 'purchase' ? attribution : null;
    const cardSetupAttribution = attribution?.flow === 'card_setup' ? attribution : null;

    const refreshCredits = () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.subscription.creditTopup] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.subscription.usage] });
    };

    if (status === TOPUP_STATUS_SUCCESS) {
      refreshCredits();
      toast.success(t('topup_panel.purchase_success'));
      capture(PostHogEventEnum.CreditTopupPurchaseConfirmed, {
        session_id: sessionId,
        ...(purchaseAttribution && {
          entrypoint: purchaseAttribution.entrypoint,
          auto_reload: purchaseAttribution.auto_reload,
          purchase_mode: purchaseAttribution.auto_reload ? 'auto_reload' : 'one_time',
          has_balance: purchaseAttribution.has_balance,
        }),
      });
      settleTimerRef.current = setTimeout(refreshCredits, WEBHOOK_SETTLE_MS);
    } else if (status === TOPUP_STATUS_AUTO_RELOAD_ON) {
      refreshCredits();
      toast.success(t('topup_panel.auto_reload.turned_on'));
      capture(PostHogEventEnum.CreditTopupCardSetupConfirmed, {
        ...(cardSetupAttribution && {
          entrypoint: cardSetupAttribution.entrypoint,
          has_balance: cardSetupAttribution.has_balance,
        }),
      });
      settleTimerRef.current = setTimeout(refreshCredits, WEBHOOK_SETTLE_MS);
    } else if (status === TOPUP_STATUS_AUTO_RELOAD_CANCELLED) {
      toast.info(t('topup_panel.purchase_cancelled'));
      capture(PostHogEventEnum.CreditTopupCardSetupCancelled, {
        ...(cardSetupAttribution && {
          entrypoint: cardSetupAttribution.entrypoint,
          has_balance: cardSetupAttribution.has_balance,
        }),
      });
    } else {
      toast.info(t('topup_panel.purchase_cancelled'));
      capture(PostHogEventEnum.CreditTopupCheckoutCancelled, {
        return_status:
          status === TOPUP_STATUS_CANCELLED ? TOPUP_STATUS_CANCELLED : TOPUP_STATUS_UNKNOWN,
        ...(purchaseAttribution && {
          entrypoint: purchaseAttribution.entrypoint,
          auto_reload: purchaseAttribution.auto_reload,
          purchase_mode: purchaseAttribution.auto_reload ? 'auto_reload' : 'one_time',
          has_balance: purchaseAttribution.has_balance,
        }),
      });
    }
    clearCreditTopupCheckoutAttribution();

    // Drop only our own params: clearing everything would lose tab=billing and bounce the user.
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM);
        next.delete(SESSION_ID_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [status, sessionId, t, capture, queryClient, setSearchParams]);
};
